/**
 * Localized marketing URLs: crawlable paths per language + mapping to internal "logical" routes.
 * Internal code (SEO_ARTICLE_PAGE_KEYS, isSeoLandingPathname) always uses English logical paths like /docusign-alternative.
 */

export const SITE_ORIGIN = 'https://primedocumentsign.com'

/** URL segment = i18n code (hreflang uses HREFLANG_BY_I18N for pt → pt-BR). */
export const LOCALE_PREFIXES = new Set(['en', 'es', 'pt', 'fr', 'de', 'it', 'ja', 'zh', 'hi', 'ar'])

/**
 * logicalPath → localized final segment(s) (no leading slash). English uses public SEO slug.
 * Home logical "/" uses empty string for en; /pt and /es are locale-only.
 */
export const LOGICAL_PATH_SLUGS = {
  '/': { en: '', pt: '', es: '', fr: '', de: '', it: '', ja: '', zh: '', hi: '', ar: '' },
  '/pricing': {
    en: 'pricing',
    pt: 'precos',
    es: 'precios',
    fr: 'tarifs',
    de: 'preise',
    it: 'prezzi',
    ja: 'pricing',
    zh: 'pricing',
    hi: 'pricing',
    ar: 'pricing',
  },
  '/esignature': {
    en: 'esignature',
    pt: 'assinatura-eletronica',
    es: 'firma-electronica',
    fr: 'signature-electronique',
    de: 'e-signatur',
    it: 'firma-elettronica',
    ja: 'e-signature',
    zh: 'e-signature',
    hi: 'e-signature',
    ar: 'e-signature',
  },
  '/docusign-alternative': {
    en: 'docusign-alternative',
    pt: 'alternativa-ao-docusign',
    es: 'alternativa-a-docusign',
    fr: 'alternative-docusign',
    de: 'docusign-alternative',
    it: 'alternativa-docusign',
    ja: 'docusign-alternative',
    zh: 'docusign-alternative',
    hi: 'docusign-alternative',
    ar: 'docusign-alternative',
  },
  '/docusign-vs-prime-document-sign': {
    en: 'docusign-vs-prime-document-sign',
    pt: 'docusign-vs-prime-document-sign',
    es: 'docusign-vs-prime-document-sign',
    fr: 'docusign-vs-prime-document-sign',
    de: 'docusign-vs-prime-document-sign',
    it: 'docusign-vs-prime-document-sign',
    ja: 'docusign-vs-prime-document-sign',
    zh: 'docusign-vs-prime-document-sign',
    hi: 'docusign-vs-prime-document-sign',
    ar: 'docusign-vs-prime-document-sign',
  },
  '/free-esignature': {
    en: 'free-esignature',
    pt: 'assinatura-eletronica-gratis',
    es: 'firma-electronica-gratis',
    fr: 'signature-electronique-gratuite',
    de: 'kostenlose-e-signatur',
    it: 'firma-elettronica-gratuita',
    ja: 'free-esignature',
    zh: 'free-esignature',
    hi: 'free-esignature',
    ar: 'free-esignature',
  },
  '/templates': {
    en: 'templates',
    pt: 'modelos',
    es: 'plantillas',
    fr: 'modeles',
    de: 'vorlagen',
    it: 'modelli',
    ja: 'templates',
    zh: 'templates',
    hi: 'templates',
    ar: 'templates',
  },
  '/templates/nda': {
    en: 'templates/nda',
    pt: 'modelos/nda',
    es: 'plantillas/nda',
    fr: 'modeles/nda',
    de: 'vorlagen/nda',
    it: 'modelli/nda',
    ja: 'templates/nda',
    zh: 'templates/nda',
    hi: 'templates/nda',
    ar: 'templates/nda',
  },
  '/templates/contract': {
    en: 'templates/contract',
    pt: 'modelos/contract',
    es: 'plantillas/contract',
    fr: 'modeles/contract',
    de: 'vorlagen/contract',
    it: 'modelli/contract',
    ja: 'templates/contract',
    zh: 'templates/contract',
    hi: 'templates/contract',
    ar: 'templates/contract',
  },
  '/templates/freelance-agreement': {
    en: 'templates/freelance-agreement',
    pt: 'modelos/freelance-agreement',
    es: 'plantillas/freelance-agreement',
    fr: 'modeles/freelance-agreement',
    de: 'vorlagen/freelance-agreement',
    it: 'modelli/freelance-agreement',
    ja: 'templates/freelance-agreement',
    zh: 'templates/freelance-agreement',
    hi: 'templates/freelance-agreement',
    ar: 'templates/freelance-agreement',
  },
  '/templates/lease-agreement': {
    en: 'templates/lease-agreement',
    pt: 'modelos/lease-agreement',
    es: 'plantillas/lease-agreement',
    fr: 'modeles/lease-agreement',
    de: 'vorlagen/lease-agreement',
    it: 'modelli/lease-agreement',
    ja: 'templates/lease-agreement',
    zh: 'templates/lease-agreement',
    hi: 'templates/lease-agreement',
    ar: 'templates/lease-agreement',
  },
  '/templates/service-agreement': {
    en: 'templates/service-agreement',
    pt: 'modelos/service-agreement',
    es: 'plantillas/service-agreement',
    fr: 'modeles/service-agreement',
    de: 'vorlagen/service-agreement',
    it: 'modelli/service-agreement',
    ja: 'templates/service-agreement',
    zh: 'templates/service-agreement',
    hi: 'templates/service-agreement',
    ar: 'templates/service-agreement',
  },
  '/templates/independent-contractor': {
    en: 'templates/independent-contractor',
    pt: 'modelos/independent-contractor',
    es: 'plantillas/independent-contractor',
    fr: 'modeles/independent-contractor',
    de: 'vorlagen/independent-contractor',
    it: 'modelli/independent-contractor',
    ja: 'templates/independent-contractor',
    zh: 'templates/independent-contractor',
    hi: 'templates/independent-contractor',
    ar: 'templates/independent-contractor',
  },
  '/templates/purchase-order': {
    en: 'templates/purchase-order',
    pt: 'modelos/purchase-order',
    es: 'plantillas/purchase-order',
    fr: 'modeles/purchase-order',
    de: 'vorlagen/purchase-order',
    it: 'modelli/purchase-order',
    ja: 'templates/purchase-order',
    zh: 'templates/purchase-order',
    hi: 'templates/purchase-order',
    ar: 'templates/purchase-order',
  },
  '/templates/photo-release': {
    en: 'templates/photo-release',
    pt: 'modelos/photo-release',
    es: 'plantillas/photo-release',
    fr: 'modeles/photo-release',
    de: 'vorlagen/photo-release',
    it: 'modelli/photo-release',
    ja: 'templates/photo-release',
    zh: 'templates/photo-release',
    hi: 'templates/photo-release',
    ar: 'templates/photo-release',
  },
  '/esignature-australia': {
    en: 'esignature-australia',
    pt: 'assinatura-eletronica-australia',
    es: 'firma-electronica-australia',
    fr: 'signature-electronique-australie',
    de: 'e-signatur-australien',
    it: 'firma-elettronica-australia',
    ja: 'esignature-australia',
    zh: 'esignature-australia',
    hi: 'esignature-australia',
    ar: 'esignature-australia',
  },
  '/esignature/real-estate': {
    en: 'esignature/real-estate',
    pt: 'assinatura-eletronica/real-estate',
    es: 'firma-electronica/real-estate',
    fr: 'signature-electronique/real-estate',
    de: 'e-signatur/real-estate',
    it: 'firma-elettronica/real-estate',
    ja: 'e-signature/real-estate',
    zh: 'e-signature/real-estate',
    hi: 'e-signature/real-estate',
    ar: 'e-signature/real-estate',
  },
  '/esignature/accountants': {
    en: 'esignature/accountants',
    pt: 'assinatura-eletronica/accountants',
    es: 'firma-electronica/accountants',
    fr: 'signature-electronique/accountants',
    de: 'e-signatur/accountants',
    it: 'firma-elettronica/accountants',
    ja: 'e-signature/accountants',
    zh: 'e-signature/accountants',
    hi: 'e-signature/accountants',
    ar: 'e-signature/accountants',
  },
  '/esignature/hr': {
    en: 'esignature/hr',
    pt: 'assinatura-eletronica/hr',
    es: 'firma-electronica/hr',
    fr: 'signature-electronique/hr',
    de: 'e-signatur/hr',
    it: 'firma-elettronica/hr',
    ja: 'e-signature/hr',
    zh: 'e-signature/hr',
    hi: 'e-signature/hr',
    ar: 'e-signature/hr',
  },
  '/esignature/agencies': {
    en: 'esignature/agencies',
    pt: 'assinatura-eletronica/agencies',
    es: 'firma-electronica/agencies',
    fr: 'signature-electronique/agencies',
    de: 'e-signatur/agencies',
    it: 'firma-elettronica/agencies',
    ja: 'e-signature/agencies',
    zh: 'e-signature/agencies',
    hi: 'e-signature/agencies',
    ar: 'e-signature/agencies',
  },
  '/security': {
    en: 'security',
    pt: 'seguranca',
    es: 'seguridad',
    fr: 'securite',
    de: 'sicherheit',
    it: 'sicurezza',
    ja: 'security',
    zh: 'security',
    hi: 'security',
    ar: 'security',
  },
  '/esignature-legal': {
    en: 'esignature-legal',
    pt: 'assinatura-eletronica-juridico',
    es: 'firma-electronica-legal',
    fr: 'signature-electronique-juridique',
    de: 'e-signatur-rechtliches',
    it: 'firma-elettronica-legale',
    ja: 'esignature-legal',
    zh: 'esignature-legal',
    hi: 'esignature-legal',
    ar: 'esignature-legal',
  },
  '/why-primedocumentsign': {
    en: 'why-primedocumentsign',
    pt: 'why-primedocumentsign',
    es: 'why-primedocumentsign',
    fr: 'why-primedocumentsign',
    de: 'why-primedocumentsign',
    it: 'why-primedocumentsign',
    ja: 'why-primedocumentsign',
    zh: 'why-primedocumentsign',
    hi: 'why-primedocumentsign',
    ar: 'why-primedocumentsign',
  },
  '/about': {
    en: 'about',
    pt: 'sobre',
    es: 'sobre',
    fr: 'a-propos',
    de: 'ueber-uns',
    it: 'chi-siamo',
    ja: 'about',
    zh: 'about',
    hi: 'about',
    ar: 'about',
  },
  '/trust': {
    en: 'trust',
    pt: 'confianca',
    es: 'confianza',
    fr: 'confiance',
    de: 'vertrauen',
    it: 'affidabilita',
    ja: 'trust',
    zh: 'trust',
    hi: 'trust',
    ar: 'trust',
  },
  '/product': {
    en: 'product',
    pt: 'produto',
    es: 'producto',
    fr: 'produit',
    de: 'produkt',
    it: 'prodotto',
    ja: 'product',
    zh: 'product',
    hi: 'product',
    ar: 'product',
  },
  '/alternatives': {
    en: 'alternatives',
    pt: 'alternativas',
    es: 'alternativas',
    fr: 'alternatives',
    de: 'alternativen',
    it: 'alternative',
    ja: 'alternatives',
    zh: 'alternatives',
    hi: 'alternatives',
    ar: 'alternatives',
  },
  '/what-is-esignature': {
    en: 'what-is-esignature',
    pt: 'o-que-e-assinatura-eletronica',
    es: 'que-es-firma-electronica',
    fr: 'qu-est-ce-signature-electronique',
    de: 'was-ist-e-signatur',
    it: 'cos-e-firma-elettronica',
    ja: 'what-is-esignature',
    zh: 'what-is-esignature',
    hi: 'what-is-esignature',
    ar: 'what-is-esignature',
  },
  '/reviews': {
    en: 'reviews',
    pt: 'avaliacoes',
    es: 'resenas',
    fr: 'avis',
    de: 'bewertungen',
    it: 'recensioni',
    ja: 'reviews',
    zh: 'reviews',
    hi: 'reviews',
    ar: 'reviews',
  },
  '/terms': {
    en: 'terms',
    pt: 'termos',
    es: 'terminos',
    fr: 'conditions',
    de: 'agb',
    it: 'termini',
    ja: 'terms',
    zh: 'terms',
    hi: 'terms',
    ar: 'terms',
  },
  '/privacy': {
    en: 'privacy',
    pt: 'privacidade',
    es: 'privacidad',
    fr: 'confidentialite',
    de: 'datenschutz',
    it: 'privacy',
    ja: 'privacy',
    zh: 'privacy',
    hi: 'privacy',
    ar: 'privacy',
  },
  '/blog': {
    en: 'blog',
    pt: 'blog',
    es: 'blog',
    fr: 'blog',
    de: 'blog',
    it: 'blog',
    ja: 'blog',
    zh: 'blog',
    hi: 'blog',
    ar: 'blog',
  },
}

/** hreflang attribute value per app locale code */
export const HREFLANG_BY_I18N = {
  en: 'en',
  es: 'es',
  pt: 'pt-BR',
  fr: 'fr',
  de: 'de',
  it: 'it',
  ja: 'ja',
  zh: 'zh-CN',
  hi: 'hi',
  ar: 'ar',
}

function trimPath(pathname) {
  if (!pathname || typeof pathname !== 'string') return '/'
  let p = pathname.trim()
  if (!p.startsWith('/')) p = `/${p}`
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
  return p || '/'
}

function buildReverseSlugIndex() {
  const map = new Map()
  for (const [logical, byLocale] of Object.entries(LOGICAL_PATH_SLUGS)) {
    for (const [loc, slug] of Object.entries(byLocale)) {
      if (logical === '/') {
        map.set(`${loc}::__root__`, '/')
        continue
      }
      const s = slug || ''
      map.set(`${loc}::${s}`, logical)
    }
  }
  return map
}

const REVERSE_SLUG = buildReverseSlugIndex()

function slugTrailToLogical(locale, segments) {
  if (segments.length === 0) return '/'
  if (segments[0] === 'blog') {
    return `/${segments.join('/')}`
  }
  const joined = segments.join('/')
  const direct = REVERSE_SLUG.get(`${locale}::${joined}`)
  if (direct) return direct
  const asEn = REVERSE_SLUG.get(`en::${joined}`)
  if (asEn) return asEn
  return `/${joined}`
}

/**
 * Parse browser pathname → UI locale + logical internal path (English-style keys).
 */
export function parseLocalePathname(pathname) {
  const raw = trimPath(pathname)
  if (raw === '/') return { locale: 'en', logicalPath: '/' }

  const parts = raw.slice(1).split('/').filter(Boolean)
  if (parts.length === 0) return { locale: 'en', logicalPath: '/' }

  const first = parts[0].toLowerCase()

  if (LOCALE_PREFIXES.has(first)) {
    const locale = first
    const rest = parts.slice(1)
    if (rest.length === 0) {
      return { locale, logicalPath: '/' }
    }
    if (locale === 'en') {
      const logical = slugTrailToLogical('en', rest)
      return { locale: 'en', logicalPath: logical }
    }
    const logical = slugTrailToLogical(locale, rest)
    return { locale, logicalPath: logical }
  }

  const logical = slugTrailToLogical('en', parts)
  return { locale: 'en', logicalPath: logical }
}

/**
 * Public URL path (with leading slash) for a logical route and UI language.
 * English has no /en/ prefix. Other locales: /{locale}/{localized-slug}.
 */
export function buildLocalizedPath(logicalPath, locale) {
  const logical = trimPath(logicalPath) === '/' ? '/' : trimPath(logicalPath)
  const row = LOGICAL_PATH_SLUGS[logical]
  if (!row) {
    if (locale === 'en') return logical === '/' ? '/' : logical
    const tail = logical.startsWith('/') ? logical.slice(1) : logical
    return `/${locale}/${tail}`
  }

  const slug = row[locale] ?? row.en

  if (locale === 'en') {
    if (logical === '/') return '/'
    return `/${slug}`
  }

  if (logical === '/') {
    return `/${locale}`
  }

  return `/${locale}/${slug}`
}

export function buildAbsoluteUrl(logicalPath, locale) {
  return `${SITE_ORIGIN}${buildLocalizedPath(logicalPath, locale)}`
}

/** Logical pathname for blog/SEO helpers (strip /pt/, map slug). */
export function toLogicalPathname(pathname) {
  return parseLocalePathname(pathname).logicalPath
}

const HREFLANG_LOCALES = Object.keys(HREFLANG_BY_I18N)

/**
 * Inject / refresh <link rel="alternate" hreflang="…"> for the current logical page.
 */
export function setHreflangAlternates(logicalPath) {
  if (typeof document === 'undefined') return
  const head = document.head
  if (!head) return

  head.querySelectorAll('link[data-pds-hreflang="1"]').forEach((el) => el.remove())

  const logical = trimPath(logicalPath) === '/' ? '/' : trimPath(logicalPath)
  const enUrl = buildAbsoluteUrl(logical, 'en')

  const add = (hreflang, href) => {
    const link = document.createElement('link')
    link.rel = 'alternate'
    link.hreflang = hreflang
    link.href = href
    link.setAttribute('data-pds-hreflang', '1')
    head.appendChild(link)
  }

  for (const loc of HREFLANG_LOCALES) {
    add(HREFLANG_BY_I18N[loc], buildAbsoluteUrl(logical, loc))
  }
  add('x-default', enUrl)
}

/** All supported public paths for sitemap (logical). */
export const SITEMAP_LOGICAL_PATHS = [
  '/',
  '/pricing',
  '/esignature',
  '/esignature/real-estate',
  '/esignature/accountants',
  '/esignature/hr',
  '/esignature/agencies',
  '/docusign-alternative',
  '/docusign-vs-prime-document-sign',
  '/free-esignature',
  '/templates',
  '/templates/nda',
  '/templates/contract',
  '/templates/freelance-agreement',
  '/templates/lease-agreement',
  '/templates/service-agreement',
  '/templates/independent-contractor',
  '/templates/purchase-order',
  '/templates/photo-release',
  '/esignature-australia',
  '/security',
  '/esignature-legal',
  '/why-primedocumentsign',
  '/about',
  '/trust',
  '/product',
  '/alternatives',
  '/what-is-esignature',
  '/reviews',
  '/terms',
  '/privacy',
  '/blog',
]
